import map from 'lodash/map';
import { assert, assertObject } from '@Utils/assert';
import SocketBroker from '@Services/SocketBroker';
import MessageBroker from '@Services/MessageBroker';
import RequestUrls from '@Constants/RequestUrlsConstants';
import { RxSocketUrls } from '@Constants/CommonConstants';
import { preprocessFiltersResponse } from '@Services/FeatureSwitchResponsePreprocessors';
import { AUTHORS_OF_SCI_DOC_FILTER_ID } from '@Containers/ResultsFilters/ResultsFiltersConstants';

export function normalizeGetAsyncResponse(filters) {
  return filters.map((item) => {
    let type = 1;
    let extension = {
      expanded: false,
      userInputOpen: false,
      userInputValue: '',
    };

    if (item.numeric) {
      type = 2;

      extension = {
        expanded: false,
        userInputOpen: false,
        userInputValue: '',
      };
    } else if (item.checkMark) {
      type = 4;

      extension = {
        selected: false,
      };
    } else if (item.structure) {
      type = 5;

      extension = {
        expanded: false,
      };
    } else if (item.fullText) {
      type = 6;
    }

    return {
      groups: [],
      groupCount: 0,
      ...item,
      ...extension,
      id: item.value,
      type,
    };
  });
}

export const getAsync = Logger.logAsync(async (params) => {
  const result = await MessageBroker.send(RequestUrls.filterTaxonomies, params);
  return normalizeGetAsyncResponse(preprocessFiltersResponse(result, params));
});

export const getRawFilterGroupsAsync = Logger.logAsync(async (params) => {
  Logger.info('getRawFilterGroupsAsync', params);
  const subject = SocketBroker.send(
    {
      send: RxSocketUrls.Filters.Group,
      message: RxSocketUrls.Filters.GroupResult,
    },
    {
      params,
      oneTime: true,
    }
  );

  return new Promise((resolve, reject) => {
    subject.subscribe({
      next: ({ type, data }) => {
        if (type === 'success') {
          Logger.info('getRawFilterGroupsAsync:resolve ', data);
          resolve(data.payload);
        }
      },
      error: reject,
    });
  });
});

export function normalizeThesaurus(payload, extensions) {
  assertObject(extensions, 'extensions');
  assert(extensions.rootIds, 'rootIds requires parameter in extension for all Thesaurus');

  const { thesaurus } = payload;
  const allChildren = map(thesaurus, 'children')
    .filter(Boolean)
    .reduce((res, item) => res.concat(item), []);

  return allChildren.map((item) => {
    const { name } = item;
    return {
      ...extensions,
      ...item,
      title: name,
    };
  });
}

export function normalizeResponseWithThesaurus(payload) {
  const { thesaurus, property } = payload;
  const { children = [], childrenCount, hitCount, id, index } = thesaurus[0];

  assert(thesaurus.length === 1, '"thesaurus" has length mote then one, potential issue with thesaurus dialog in filter results');

  const normalizedGroups = children.map((item) => {
    const { name: title, hitCount: size } = item;

    return {
      ...item,
      selected: false,
      title,
      size,
    };
  });

  return {
    property,
    hitCount,
    index,
    thesaurusRootId: id,
    thesaurusGroups: normalizeThesaurus(payload, {
      rootIds: [id],
    }),
    groups: normalizedGroups,
    groupCount: childrenCount,
  };
}
export function normalizeGroupsResponse(payload, params) {
  const filterId = params.properties[0] && params.properties[0].property;

  const { groups = [], groupCount } = payload;
  const normalizedGroups = groups?.map((item) => ({
    ...item,
    sizeAsInteger: parseInt(item.size, 10),
    selected: false,
    // There is no unique id for the group, so we use the index and title
    id: filterId === AUTHORS_OF_SCI_DOC_FILTER_ID ? `${item.index}/${item.title}` : item.title,
  }));

  return {
    groups: normalizedGroups,
    groupCount,
  };
}

export function normalizeGetFilterGroupsAsyncResponse(payload, params) {
  const { thesaurus } = payload;

  if (thesaurus) {
    return normalizeResponseWithThesaurus(payload);
  }

  return normalizeGroupsResponse(payload, params);
}

export async function getNormalizedFilterGroupsAsync(params) {
  const result = await getRawFilterGroupsAsync(params);

  return normalizeGetFilterGroupsAsyncResponse(result, params);
}

export async function getNormalizedFilterThesaurusAsync(params, extensions) {
  assertObject(params, 'params');
  assertObject(extensions, 'extensions');

  const result = await getRawFilterGroupsAsync(params);

  return normalizeThesaurus(result, extensions);
}

function normalizeSearchFilterAsync(data) {
  const { payload } = data;

  return payload;
}

export const searchFilterAsync = Logger.logAsync(async (params) => {
  const subject = SocketBroker.send(
    {
      send: RxSocketUrls.Filters.SearchFilter,
      message: RxSocketUrls.ChannelsUrls.SearchInfo,
    },
    {
      params,
      oneTime: true,
    }
  );

  return new Promise((resolve, reject) => {
    subject.subscribe({
      next: ({ data }) => resolve(normalizeSearchFilterAsync(data)),
      error: reject,
    });
  });
});

export const loadLimitResults = async (limitResultsParams) =>
  new Promise((resolve, reject) => {
    const subject = SocketBroker.send(
      {
        send: RxSocketUrls.Filters.SearchLimit,
        message: RxSocketUrls.ChannelsUrls.SearchInfo,
      },
      {
        params: limitResultsParams,
        oneTime: true,
      }
    );

    subject.subscribe({
      next: ({ data }) => resolve(data.payload),
      error: reject,
    });
  });
